import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const DEFAULT_MIN_YEAR = 1900;
const FUTURE_YEARS = 10;
const POPOVER_HEIGHT = 400;
const invalidInputClass = '!border-[#C0392B] border-2';
const SLOT_POSITIONS = [0, 1, 3, 4, 6, 7, 8, 9];
const SLOT_TEMPLATE = 'DD/MM/YYYY';

const pad = (number) => String(number).padStart(2, '0');

const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

const fromJsDate = (date) => ({ year: date.getFullYear(), month: date.getMonth(), day: date.getDate() });

const toIso = ({ year, month, day }) => `${year}-${pad(month + 1)}-${pad(day)}`;

const formatDisplay = (date) => (date ? `${pad(date.day)}/${pad(date.month + 1)}/${date.year}` : '');

function parseIso(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (year < 1000 || month < 0 || month > 11 || day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

function parseDisplay(text) {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text || '');
  if (!match) return null;
  return parseIso(`${match[3]}-${match[2]}-${match[1]}`);
}

function maskInput(raw, isDeleting, padSegments) {
  const trimmed = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed) && parseIso(trimmed)) return formatDisplay(parseIso(trimmed));

  const segments = raw.split(/[/.\-\s]/);
  const digits = segments
    .map((segment, index) => {
      const segmentDigits = segment.replace(/\D/g, '');
      return padSegments && index < 2 && index < segments.length - 1 && segmentDigits.length === 1 ? `0${segmentDigits}` : segmentDigits;
    })
    .join('')
    .slice(0, 8);

  let masked = digits.slice(0, 2);
  if (digits.length > 2) masked += `/${digits.slice(2, 4)}`;
  if (digits.length > 4) masked += `/${digits.slice(4)}`;
  if (!isDeleting && (digits.length === 2 || digits.length === 4)) masked += '/';
  return masked;
}

const digitsOf = (value) => value.replace(/\D/g, '');

const textToSlots = (value) => SLOT_POSITIONS.map((position) => (/\d/.test(value[position] || '') ? value[position] : null));

function slotsToText(slots, appendSeparator) {
  const lastFilled = slots.reduce((last, digit, index) => (digit ? index : last), -1);
  if (lastFilled < 0) return '';
  const text = slots
    .slice(0, lastFilled + 1)
    .map((digit, index) => `${index === 2 || index === 4 ? '/' : ''}${digit ?? SLOT_TEMPLATE[SLOT_POSITIONS[index]]}`)
    .join('');
  return appendSeparator && (lastFilled === 1 || lastFilled === 3) ? `${text}/` : text;
}

const firstSlotFrom = (position) => SLOT_POSITIONS.findIndex((slotPosition) => slotPosition >= position);

const lastSlotBefore = (position) => SLOT_POSITIONS.reduce((found, slotPosition, index) => (slotPosition < position ? index : found), -1);

function caretAfterDigits(masked, digitCount, isDeleting) {
  if (digitCount === 0) return 0;
  let seen = 0;
  for (let index = 0; index < masked.length; index += 1) {
    if (/\d/.test(masked[index])) {
      seen += 1;
      if (seen === digitCount) {
        const position = index + 1;
        return !isDeleting && masked[position] === '/' ? position + 1 : position;
      }
    }
  }
  return masked.length;
}

const addDays = (date, amount) => fromJsDate(new Date(date.year, date.month, date.day + amount));

function addMonths(date, amount) {
  const target = new Date(date.year, date.month + amount, 1);
  const year = target.getFullYear();
  const month = target.getMonth();
  return { year, month, day: Math.min(date.day, daysInMonth(year, month)) };
}

const weekdayIndex = (date) => (new Date(date.year, date.month, date.day).getDay() + 6) % 7;

const normalizeIso = (value) => {
  const date = parseIso(value);
  return date ? toIso(date) : '';
};

const getTodayIso = () => toIso(fromJsDate(new Date()));

function CalendarIcon() {
  return (
    <svg className='h-[18px] w-[18px]' fill='none' stroke='currentColor' viewBox='0 0 24 24' aria-hidden='true'>
      <rect x='3.5' y='5' width='17' height='15.5' rx='2.5' strokeWidth={1.8} />
      <path strokeLinecap='round' strokeWidth={1.8} d='M3.5 10h17M8 3v4M16 3v4' />
    </svg>
  );
}

function ChevronIcon({ direction }) {
  const paths = { left: 'M15 18l-6-6 6-6', right: 'M9 6l6 6-6 6', down: 'M6 9l6 6 6-6' };
  return (
    <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24' aria-hidden='true'>
      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d={paths[direction]} />
    </svg>
  );
}

ChevronIcon.propTypes = {
  direction: PropTypes.oneOf(['left', 'right', 'down']).isRequired,
};

function DatePicker({ id, name, value = '', onChange, min, max, disabled = false, placeholder = 'DD/MM/YYYY', className = '' }) {
  const generatedId = useId();
  const inputId = id || generatedId;
  const dialogId = `${inputId}-calendar`;

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const popoverRef = useRef(null);
  const gridRef = useRef(null);
  const yearsRef = useRef(null);
  const lastEmittedRef = useRef(null);
  const focusGridRef = useRef(false);

  const [text, setText] = useState('');
  const [isInvalid, setIsInvalid] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('days');
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date().getMonth());
  const [activeIso, setActiveIso] = useState('');
  const [placement, setPlacement] = useState('down');
  const [caret, setCaret] = useState(null);

  const minIso = normalizeIso(min);
  const maxIso = normalizeIso(max);
  const selectedIso = normalizeIso(value);
  const todayIso = getTodayIso();

  const isOutOfRange = useCallback((iso) => Boolean((minIso && iso < minIso) || (maxIso && iso > maxIso)), [minIso, maxIso]);

  useEffect(() => {
    const nextValue = value || '';
    if (nextValue === lastEmittedRef.current) return;
    lastEmittedRef.current = nextValue;
    setText(formatDisplay(parseIso(nextValue)));
    setIsInvalid(false);
  }, [value]);

  useLayoutEffect(() => {
    if (!caret || document.activeElement !== inputRef.current) return;
    inputRef.current.setSelectionRange(caret.position, caret.position);
  }, [caret]);

  const emit = (iso) => {
    lastEmittedRef.current = iso;
    if (iso !== (value || '')) onChange({ target: { name, value: iso } });
  };

  const showDate = (date) => {
    setViewYear(date.year);
    setViewMonth(date.month);
    setActiveIso(toIso(date));
  };

  const clampToRange = (date) => {
    const iso = toIso(date);
    if (minIso && iso < minIso) return parseIso(minIso);
    if (maxIso && iso > maxIso) return parseIso(maxIso);
    return date;
  };

  const closeCalendar = useCallback((restoreFocus = false) => {
    setIsOpen(false);
    focusGridRef.current = false;
    if (restoreFocus) inputRef.current?.focus();
  }, []);

  const openCalendar = (focusGrid = false) => {
    if (disabled) return;
    showDate(parseIso(selectedIso) || clampToRange(parseIso(todayIso)));
    setView('days');
    focusGridRef.current = focusGrid;
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setPlacement(spaceBelow < POPOVER_HEIGHT && rect.top > spaceBelow ? 'up' : 'down');
    }
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const popover = popoverRef.current;
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) closeCalendar();
    };
    const keepInputFocus = (event) => event.preventDefault();
    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closeCalendar(true);
    };
    document.addEventListener('mousedown', handleClickOutside);
    popover?.addEventListener('mousedown', keepInputFocus);
    popover?.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      popover?.removeEventListener('mousedown', keepInputFocus);
      popover?.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, closeCalendar]);

  useEffect(() => {
    if (!isOpen || view !== 'days' || !focusGridRef.current || !gridRef.current) return;
    gridRef.current.querySelector(`[data-date="${activeIso}"]`)?.focus();
  }, [isOpen, view, activeIso]);

  useEffect(() => {
    if (!isOpen || view !== 'years' || !yearsRef.current) return;
    const container = yearsRef.current;
    const yearButton = container.querySelector(`[data-year="${viewYear}"]`);
    if (yearButton) container.scrollTop = yearButton.offsetTop - container.clientHeight / 2 + yearButton.clientHeight / 2;
  }, [isOpen, view, viewYear]);

  const selectDate = (date) => {
    setText(formatDisplay(date));
    setIsInvalid(false);
    emit(toIso(date));
    closeCalendar(true);
  };

  const handleClear = () => {
    setText('');
    setIsInvalid(false);
    emit('');
    closeCalendar(true);
  };

  const applyEdit = (nextText, caretPosition) => {
    const parsed = parseDisplay(nextText);
    setText(nextText);
    setCaret({ position: Math.min(caretPosition, nextText.length) });
    setIsInvalid(false);
    emit(parsed ? toIso(parsed) : '');
    if (parsed && isOpen) {
      showDate(parsed);
      setView('days');
    }
  };

  const handleInputChange = (e) => {
    const raw = e.target.value;
    const caretIndex = e.target.selectionStart ?? raw.length;
    const isDeleting = raw.length < text.length;
    const isCaretAtEnd = caretIndex >= raw.length;
    const masked = maskInput(raw, isDeleting, isCaretAtEnd);
    applyEdit(masked, isCaretAtEnd ? masked.length : caretAfterDigits(masked, digitsOf(raw.slice(0, caretIndex)).length, isDeleting));
  };

  const handleBeforeInput = (e) => {
    const { inputType } = e;
    const start = inputRef.current.selectionStart ?? text.length;
    const end = inputRef.current.selectionEnd ?? start;
    const slots = textToSlots(text);
    const clearRange = (from, to) =>
      SLOT_POSITIONS.forEach((position, index) => {
        if (position >= from && position < to) slots[index] = null;
      });
    const insertDigits = (digits) => {
      let slot = firstSlotFrom(start);
      if (slot < 0 || !digits) return;
      clearRange(start, end);
      let caretPosition = start;
      [...digits].forEach((digit) => {
        if (slot < 0 || slot >= SLOT_POSITIONS.length) return;
        slots[slot] = digit;
        caretPosition = SLOT_POSITIONS[slot] + 1;
        slot += 1;
      });
      applyEdit(slotsToText(slots, true), caretPosition === 2 || caretPosition === 5 ? caretPosition + 1 : caretPosition);
    };

    if (inputType === 'insertText') {
      e.preventDefault();
      const data = e.data || '';
      if (/^\d+$/.test(data)) {
        insertDigits(data);
        return;
      }
      if (!/^[/.\-\s]$/.test(data) || start !== end) return;
      const lastFilled = slots.reduce((last, digit, index) => (digit ? index : last), -1);
      if (start === text.length && (lastFilled === 0 || lastFilled === 2) && text.length === SLOT_POSITIONS[lastFilled] + 1) {
        slots[lastFilled + 1] = slots[lastFilled];
        slots[lastFilled] = '0';
        applyEdit(slotsToText(slots, true), SLOT_POSITIONS[lastFilled + 1] + 2);
      } else if (text[start] === '/') {
        setCaret({ position: start + 1 });
      }
      return;
    }

    if (inputType.startsWith('delete')) {
      e.preventDefault();
      let caretPosition = start;
      if (start !== end) {
        clearRange(start, end);
      } else if (inputType === 'deleteContentBackward') {
        const slot = lastSlotBefore(start);
        if (slot < 0) return;
        slots[slot] = null;
        caretPosition = SLOT_POSITIONS[slot];
      } else if (inputType === 'deleteContentForward') {
        const slot = firstSlotFrom(start);
        if (slot < 0) return;
        slots[slot] = null;
      } else if (inputType.endsWith('Backward')) {
        clearRange(0, start);
        caretPosition = 0;
      } else {
        clearRange(start, text.length);
      }
      applyEdit(slotsToText(slots, false), caretPosition);
      return;
    }

    if (inputType === 'insertFromPaste' || inputType === 'insertFromDrop' || inputType === 'insertReplacementText') {
      e.preventDefault();
      const pasted = (e.data ?? e.dataTransfer?.getData('text/plain') ?? '').trim();
      const pastedDate = parseDisplay(maskInput(pasted, false, true));
      if (pastedDate && (start === 0 || !text) && end >= text.length) {
        applyEdit(formatDisplay(pastedDate), 10);
        return;
      }
      insertDigits(digitsOf(pasted));
    }
  };

  const beforeInputRef = useRef(handleBeforeInput);
  beforeInputRef.current = handleBeforeInput;

  useEffect(() => {
    const input = inputRef.current;
    const listener = (event) => beforeInputRef.current(event);
    input.addEventListener('beforeinput', listener);
    return () => input.removeEventListener('beforeinput', listener);
  }, []);

  const handleInputKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        openCalendar(true);
        return;
      }
      focusGridRef.current = true;
      setView('days');
      gridRef.current?.querySelector(`[data-date="${activeIso}"]`)?.focus();
    } else if ((e.key === 'Escape' || e.key === 'Enter') && isOpen) {
      e.preventDefault();
      closeCalendar();
    }
  };

  const handleWrapperBlur = (e) => {
    if (wrapperRef.current?.contains(e.relatedTarget)) return;
    closeCalendar();
    if (text && !parseDisplay(text)) setIsInvalid(true);
  };

  const handleIconClick = () => {
    if (isOpen) {
      closeCalendar(true);
      return;
    }
    inputRef.current?.focus();
    openCalendar(false);
  };

  const handleGridKeyDown = (e) => {
    const current = parseIso(activeIso);
    if (!current) return;
    const moves = {
      ArrowLeft: () => addDays(current, -1),
      ArrowRight: () => addDays(current, 1),
      ArrowUp: () => addDays(current, -7),
      ArrowDown: () => addDays(current, 7),
      PageUp: () => addMonths(current, e.shiftKey ? -12 : -1),
      PageDown: () => addMonths(current, e.shiftKey ? 12 : 1),
      Home: () => addDays(current, -weekdayIndex(current)),
      End: () => addDays(current, 6 - weekdayIndex(current)),
    };
    if (!moves[e.key]) return;
    e.preventDefault();
    focusGridRef.current = true;
    showDate(clampToRange(moves[e.key]()));
  };

  const changeMonth = (amount) => {
    const current = parseIso(activeIso);
    showDate(addMonths({ year: viewYear, month: viewMonth, day: current ? current.day : 1 }, amount));
  };

  const selectYear = (year) => {
    setViewYear(year);
    setView('months');
  };

  const selectMonth = (month) => {
    const current = parseIso(activeIso);
    showDate(clampToRange({ year: viewYear, month, day: Math.min(current ? current.day : 1, daysInMonth(viewYear, month)) }));
    setView('days');
  };

  const weeks = useMemo(() => {
    const offset = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7;
    return Array.from({ length: 6 }, (_, week) =>
      Array.from({ length: 7 }, (__, weekday) => fromJsDate(new Date(viewYear, viewMonth, 1 - offset + week * 7 + weekday))),
    );
  }, [viewYear, viewMonth]);

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const firstYear = Math.min(minIso ? parseIso(minIso).year : DEFAULT_MIN_YEAR, viewYear);
    const lastYear = Math.max(maxIso ? parseIso(maxIso).year : currentYear + FUTURE_YEARS, viewYear);
    return Array.from({ length: lastYear - firstYear + 1 }, (_, index) => firstYear + index);
  }, [minIso, maxIso, viewYear]);

  const isPrevDisabled =
    view === 'months'
      ? Boolean(minIso && `${viewYear - 1}-12-31` < minIso)
      : Boolean(minIso && toIso(fromJsDate(new Date(viewYear, viewMonth, 0))) < minIso);
  const isNextDisabled =
    view === 'months'
      ? Boolean(maxIso && `${viewYear + 1}-01-01` > maxIso)
      : Boolean(maxIso && toIso(fromJsDate(new Date(viewYear, viewMonth + 1, 1))) > maxIso);

  const handlePrev = () => (view === 'months' ? setViewYear(viewYear - 1) : changeMonth(-1));
  const handleNext = () => (view === 'months' ? setViewYear(viewYear + 1) : changeMonth(1));

  const titleLabel = { days: `${MONTHS[viewMonth]} ${viewYear}`, months: String(viewYear), years: 'Select year' }[view];
  const navButtonClass =
    'inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#3D5A73] transition hover:bg-[#EBF4FB] hover:text-[#0B1929] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2E6BAD]/40 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent';
  const footerButtonClass =
    'rounded-md px-2.5 py-1.5 text-[13px] font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2E6BAD]/40 disabled:cursor-not-allowed disabled:opacity-40';

  const getDayClass = (iso, inMonth, outOfRange) => {
    const base =
      'flex h-9 w-full items-center justify-center rounded-lg text-[13px] tabular-nums transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2E6BAD] focus-visible:ring-offset-1';
    if (iso === selectedIso) return `${base} bg-[#0B1929] font-semibold text-white shadow-[0_2px_6px_rgba(11,25,41,0.25)] hover:bg-[#0F2540]`;
    if (outOfRange) return `${base} cursor-not-allowed text-[#7A9AB0]/35`;
    const tone = inMonth ? 'font-medium text-[#0B1929]' : 'text-[#7A9AB0]/60';
    const today = iso === todayIso ? 'font-semibold text-[#2E6BAD] ring-1 ring-inset ring-[#2E6BAD]/40' : '';
    return `${base} ${tone} ${today} hover:bg-[#EBF4FB]`;
  };

  const getTileClass = (isSelected, isCurrent, isDisabled) => {
    const base =
      'flex w-full items-center justify-center rounded-lg text-[13px] font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2E6BAD]';
    if (isSelected) return `${base} bg-[#0B1929] text-white hover:bg-[#0F2540]`;
    if (isDisabled) return `${base} cursor-not-allowed text-[#7A9AB0]/35`;
    return `${base} ${isCurrent ? 'text-[#2E6BAD] ring-1 ring-inset ring-[#2E6BAD]/40' : 'text-[#0B1929]'} hover:bg-[#EBF4FB]`;
  };

  const selectedDate = parseIso(selectedIso);
  const today = parseIso(todayIso);

  return (
    <div ref={wrapperRef} className={`relative w-full ${isOpen ? 'z-50' : ''}`} onBlur={handleWrapperBlur}>
      <div className='relative'>
        <input
          ref={inputRef}
          id={inputId}
          name={name}
          type='text'
          role='combobox'
          inputMode='numeric'
          autoComplete='off'
          placeholder={placeholder}
          value={text}
          disabled={disabled}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onClick={() => !isOpen && openCalendar(false)}
          aria-haspopup='dialog'
          aria-expanded={isOpen}
          aria-controls={isOpen ? dialogId : undefined}
          aria-invalid={isInvalid || undefined}
          className={`${className} pr-11 tabular-nums ${isInvalid ? invalidInputClass : ''}`}
        />
        <button
          type='button'
          tabIndex={-1}
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleIconClick}
          aria-label={isOpen ? 'Close calendar' : 'Open calendar'}
          className={`absolute right-1.5 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg transition hover:bg-[#EBF4FB] hover:text-[#2E6BAD] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent ${
            isOpen ? 'bg-[#EBF4FB] text-[#2E6BAD]' : 'text-[#7A9AB0]'
          }`}
        >
          <CalendarIcon />
        </button>

        {isOpen && (
          <div
            ref={popoverRef}
            id={dialogId}
            role='dialog'
            aria-label='Choose date'
            className={`absolute left-0 z-[1000] w-[312px] max-w-[calc(100vw-2rem)] animate-date-picker-in rounded-[14px] border-[1.5px] border-[#DDE6EF] bg-white p-3 shadow-[0_12px_32px_rgba(11,25,41,0.16)] ${
              placement === 'up' ? 'bottom-full mb-2 origin-bottom-left' : 'top-full mt-2 origin-top-left'
            }`}
          >
            <div className='mb-2 flex items-center justify-between gap-1'>
              <button
                type='button'
                onClick={handlePrev}
                disabled={view === 'years' || isPrevDisabled}
                aria-label={view === 'months' ? 'Previous year' : 'Previous month'}
                className={`${navButtonClass} ${view === 'years' ? 'invisible' : ''}`}
              >
                <ChevronIcon direction='left' />
              </button>
              <button
                type='button'
                onClick={() => setView(view === 'years' ? 'days' : 'years')}
                aria-label={view === 'years' ? 'Back to calendar' : 'Choose year'}
                className='inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-[#0B1929] transition hover:bg-[#EBF4FB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2E6BAD]/40'
              >
                <span aria-live='polite'>{titleLabel}</span>
                <span className={`text-[#7A9AB0] transition-transform ${view === 'days' ? '' : 'rotate-180'}`}>
                  <ChevronIcon direction='down' />
                </span>
              </button>
              <button
                type='button'
                onClick={handleNext}
                disabled={view === 'years' || isNextDisabled}
                aria-label={view === 'months' ? 'Next year' : 'Next month'}
                className={`${navButtonClass} ${view === 'years' ? 'invisible' : ''}`}
              >
                <ChevronIcon direction='right' />
              </button>
            </div>

            <div className='h-[264px]'>
              {view === 'days' && (
                <div ref={gridRef} role='grid' tabIndex={-1} aria-label={titleLabel} onKeyDown={handleGridKeyDown} className='focus:outline-none'>
                  <div role='row' className='mb-1 grid grid-cols-7'>
                    {WEEKDAYS.map((weekday) => (
                      <span
                        key={weekday}
                        role='columnheader'
                        className='py-1 text-center text-[11px] font-semibold uppercase tracking-wide text-[#7A9AB0]'
                      >
                        {weekday}
                      </span>
                    ))}
                  </div>
                  {weeks.map((week) => (
                    <div key={toIso(week[0])} role='row' className='grid grid-cols-7 gap-0.5'>
                      {week.map((date) => {
                        const iso = toIso(date);
                        const outOfRange = isOutOfRange(iso);
                        return (
                          <div key={iso} role='gridcell' aria-selected={iso === selectedIso} className='py-px'>
                            <button
                              type='button'
                              data-date={iso}
                              tabIndex={iso === activeIso ? 0 : -1}
                              aria-disabled={outOfRange || undefined}
                              aria-current={iso === todayIso ? 'date' : undefined}
                              aria-label={new Date(date.year, date.month, date.day).toLocaleDateString('en-GB', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              })}
                              onClick={() => !outOfRange && selectDate(date)}
                              className={getDayClass(iso, date.month === viewMonth, outOfRange)}
                            >
                              {date.day}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}

              {view === 'months' && (
                <div className='grid h-full grid-cols-3 gap-2 py-1'>
                  {MONTHS.map((monthName, month) => {
                    const isDisabled = Boolean(
                      (minIso && toIso({ year: viewYear, month, day: daysInMonth(viewYear, month) }) < minIso) ||
                        (maxIso && toIso({ year: viewYear, month, day: 1 }) > maxIso),
                    );
                    return (
                      <button
                        key={monthName}
                        type='button'
                        disabled={isDisabled}
                        onClick={() => selectMonth(month)}
                        className={getTileClass(
                          selectedDate?.year === viewYear && selectedDate?.month === month,
                          today.year === viewYear && today.month === month,
                          isDisabled,
                        )}
                      >
                        {monthName.slice(0, 3)}
                      </button>
                    );
                  })}
                </div>
              )}

              {view === 'years' && (
                <div ref={yearsRef} className='relative grid h-full grid-cols-4 content-start gap-1.5 overflow-y-auto pr-1'>
                  {years.map((year) => {
                    const isDisabled = Boolean((minIso && `${year}-12-31` < minIso) || (maxIso && `${year}-01-01` > maxIso));
                    return (
                      <button
                        key={year}
                        type='button'
                        data-year={year}
                        disabled={isDisabled}
                        onClick={() => selectYear(year)}
                        className={`h-10 ${getTileClass(selectedDate?.year === year, today.year === year, isDisabled)}`}
                      >
                        {year}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className='mt-2 flex items-center justify-between border-t border-[#EEF3F8] pt-2.5'>
              <button
                type='button'
                onClick={handleClear}
                disabled={!text}
                className={`${footerButtonClass} text-[#7A9AB0] hover:bg-[#F4F7FA] hover:text-[#3D5A73]`}
              >
                Clear
              </button>
              <button
                type='button'
                onClick={() => selectDate(today)}
                disabled={isOutOfRange(todayIso)}
                className={`${footerButtonClass} text-[#2E6BAD] hover:bg-[#EBF4FB]`}
              >
                Today
              </button>
            </div>
          </div>
        )}
      </div>
      {isInvalid && <p className='mt-1.5 text-xs text-[#C0392B]'>Enter a valid date as DD/MM/YYYY</p>}
    </div>
  );
}

DatePicker.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.string,
  max: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
};

export default DatePicker;
